import React from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useFetchAttemptsAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import { Alert, Card, Spin } from 'antd'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import { useThemeColors } from '../../../../../../../hooks/useThemeColors'

const AttemptsHistogram = ({ token, showOnlyCurrentStudents = false }) => {
    const colors = useThemeColors()
    const { data: analytics, isFetching, error } = useFetchAttemptsAnalyticsQuery({ token })

    const data = analytics && [
        ...analytics.current.map((analyticsItem, index) => ({
            numberOfAttempts: `${analyticsItem.numberOfAttempts}${index === analytics.current.length - 1 ? '+' : ''}`,
            percentOfStudents: analyticsItem.percentOfStudents,
            numberOfStudents: analyticsItem.numberOfStudents,
            name: showOnlyCurrentStudents ? 'Students' : 'My students',
            fill: colors.primary,
        })),
        ...(showOnlyCurrentStudents
            ? []
            : analytics.all.map((analyticsItem, index) => ({
                  numberOfAttempts: `${analyticsItem.numberOfAttempts}${index === analytics.all.length - 1 ? '+' : ''}`,
                  percentOfStudents: analyticsItem.percentOfStudents,
                  numberOfStudents: analyticsItem.numberOfStudents,
                  name: 'All students',
                  fill: colors.success,
              }))),
    ]

    // Custom tooltip component
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const numberOfAttempts = payload[0]?.payload?.numberOfAttempts;
            const entries = payload.filter(entry => entry.value !== undefined && entry.value !== null);
            
            if (!entries.length || !numberOfAttempts) return null;
            
            return (
                <Card size='small'>
                    <Title level={5} style={{ margin: 0, fontSize: '0.9rem' }}>
                        {`${numberOfAttempts} attempts`}
                    </Title>
                    {entries.map((entry, index) => {
                        // Find original data with number of students
                        const originalData = data.find(item => 
                            item.numberOfAttempts === numberOfAttempts && 
                            item.name === entry.dataKey
                        );
                        
                        return (
                            <Text key={index} style={{ display: 'block' }}>
                                {`${entry.name}: ${entry.value}% - ${originalData?.numberOfStudents || 0} students`}
                            </Text>
                        );
                    })}
                </Card>
            );
        }
        return null;
    };

    // Custom legend that includes the number of students
    const renderLegend = props => {
        const { payload } = props
        return (
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', justifyContent: 'center', gap: '20px' }}>
                {payload.map((entry, index) => {
                    const label = entry.dataKey === 'My students' 
                        ? `My students (${analytics?.numberOfCurrentStudents})` 
                        : entry.dataKey === 'Students' 
                            ? `Students (${analytics?.numberOfCurrentStudents})` 
                            : `All students (${analytics?.numberOfAllStudents})`
                    return (
                        <li key={`item-${index}`} style={{ display: 'flex', alignItems: 'center' }}>
                            <div
                                style={{
                                    width: 10,
                                    height: 10,
                                    backgroundColor: entry.color,
                                    marginRight: 5,
                                }}
                            />
                            <Text>{label}</Text>
                        </li>
                    )
                })}
            </ul>
        )
    }

    // Generate a detailed description of the data for accessibility
    const generateDescription = () => {
        if (!data) return 'Loading data...'
        let description = ''
        if (analytics?.numberOfCurrentStudents)
            description += `Total number of ${showOnlyCurrentStudents ? '' : 'my'} students: ${
                analytics.numberOfCurrentStudents
            } students. `
        if (analytics?.numberOfAllStudents)
            description += `Total number of all students: ${analytics.numberOfAllStudents} students. `
        description += "Detailed breakdown of students' attempts. "
        data.forEach(item => {
            description += `${item.numberOfStudents} attempts were made by ${item.numberOfStudents} of the ${item.name}, making up ${item.percentOfStudents}% of the total. `
        })
        return description
    }

    if (error) return <Alert message={error} type='error' showIcon />

    // Group data by attempt number and create series for each student group
    const chartData = data && analytics ? 
        Array.from(new Set(data.map(item => item.numberOfAttempts))).map(attempt => {
            const result = { numberOfAttempts: attempt };
            const myStudentsItem = data.find(item => item.numberOfAttempts === attempt && (item.name === 'My students' || item.name === 'Students'));
            const allStudentsItem = data.find(item => item.numberOfAttempts === attempt && item.name === 'All students');
            
            if (myStudentsItem) {
                result[myStudentsItem.name] = myStudentsItem.percentOfStudents;
                result[`${myStudentsItem.name}-count`] = myStudentsItem.numberOfStudents;
            }
            
            if (allStudentsItem) {
                result['All students'] = allStudentsItem.percentOfStudents;
                result[`All students-count`] = allStudentsItem.numberOfStudents;
            }
            
            return result;
        }) : [];

    return (
        <Spin spinning={isFetching} tip='Loading attempts analytics...'>
            <div
                role='img'
                aria-label='Histogram showing the distribution of student attempts'
                aria-describedby='attemptsChartDescription'
            >
                <ResponsiveContainer width="100%" height={480}>
                    <BarChart 
                        data={chartData} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="numberOfAttempts"
                            label={{ 
                                value: 'Number of Attempts', 
                                position: 'bottom', 
                                offset: 0,
                                dy: 20
                            }}
                        />
                        <YAxis 
                            tickFormatter={value => `${value}%`}
                            label={{ 
                                value: 'Percent of Students', 
                                angle: -90, 
                                position: 'insideLeft',
                                dx: -20
                            }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        {!showOnlyCurrentStudents && (
                            <Bar 
                                dataKey="All students" 
                                fill={colors.success} 
                                radius={[4, 4, 0, 0]}
                                name="All students"
                                label={{ 
                                    position: 'top', 
                                    formatter: value => `${value}%`,
                                    fill: '#000'
                                }}
                            />
                        )}
                        <Bar 
                            dataKey={showOnlyCurrentStudents ? "Students" : "My students"} 
                            fill={colors.primary} 
                            radius={[4, 4, 0, 0]} 
                            name={showOnlyCurrentStudents ? "Students" : "My students"}
                            label={{ 
                                position: 'top', 
                                formatter: value => `${value}%`,
                                fill: '#000'
                            }}
                        />
                    </BarChart>
                </ResponsiveContainer>
                <div id='attemptsChartDescription' className='visually-hidden focusable' tabIndex='0'>
                    {generateDescription()}
                </div>
            </div>
        </Spin>
    )
}

export default AttemptsHistogram
